import { FormEvent, useEffect, useState } from 'react';
import { api, Contribution, Pack } from '../api';
import { Icon } from '../Icon';

/**
 * Write a question for the bank.
 *
 * What is submitted here does NOT become answerable. It goes to a review queue,
 * and an editor publishes it through the same import pipeline as everything
 * else — validation, rights attestation, immutable bank version. That is the
 * guarantee the whole engine rests on: no unreviewed item is ever selected into
 * someone's assessment.
 *
 * The form validates to the same shape the pipeline demands, so an author is
 * told what is wrong while they can still fix it, rather than weeks later.
 */

const LETTERS = ['a', 'b', 'c', 'd'] as const;

export function Contribute({ packs, onBack }: { packs: Pack[]; onBack: () => void }) {
  const [packId, setPackId] = useState(packs[0]?.id ?? '');
  const [topicName, setTopicName] = useState('');
  const [stem, setStem] = useState('');
  const [options, setOptions] = useState<Record<string, string>>({ a: '', b: '', c: '', d: '' });
  const [correct, setCorrect] = useState('');
  const [rationale, setRationale] = useState('');
  const [mine, setMine] = useState<Contribution[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const refresh = () => api.get<Contribution[]>('/me/contributions').then(setMine).catch(() => {});
  useEffect(() => { void refresh(); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setSent(false);
    try {
      await api.post('/contributions', {
        packId, topicName: topicName.trim() || undefined, stem,
        options: LETTERS.filter((k) => options[k]?.trim()).map((k) => ({ key: k, text: options[k]! })),
        correctResponse: correct ? [correct] : [],
        rationale: rationale.trim() || undefined,
      });
      setSent(true);
      setStem(''); setOptions({ a: '', b: '', c: '', d: '' }); setCorrect(''); setRationale('');
      setTopicName('');
      await refresh();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  const field = {
    width: '100%', font: 'inherit', padding: '0.5rem', minHeight: '2.75rem',
    border: '1px solid var(--border-strong)', borderRadius: '8px',
    background: 'var(--surface)', color: 'var(--ink)',
  } as const;

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Write a question</h1>
      <p className="muted">
        Questions you write are reviewed by an editor before anyone sees them. If yours is accepted
        and published, you earn <strong>5 cookies</strong>. Images are not supported yet — text only
        for now.
      </p>

      <form onSubmit={submit} className="card">
        <label htmlFor="c-pack">Subject</label>
        <select id="c-pack" value={packId} onChange={(e) => setPackId(e.target.value)} style={field}>
          {packs.map((p) => <option key={p.id} value={p.id}>{p.subject}</option>)}
        </select>

        <label htmlFor="c-topic" style={{ marginTop: '1rem' }}>
          Topic <span className="muted">(optional, e.g. Musculoskeletal)</span>
        </label>
        <input id="c-topic" value={topicName} onChange={(e) => setTopicName(e.target.value)} style={field} />

        <label htmlFor="c-stem" style={{ marginTop: '1rem' }}>The question</label>
        <textarea id="c-stem" rows={3} value={stem} onChange={(e) => setStem(e.target.value)}
                  style={field} aria-describedby="c-stem-help" />
        <p id="c-stem-help" className="muted" style={{ marginTop: '0.4rem' }}>
          Write it as you would want to be asked it. Avoid copying wording from any published
          question bank or examination.
        </p>

        <fieldset style={{ marginTop: '1rem' }}>
          <legend>Answer options</legend>
          <p className="muted" style={{ marginTop: 0 }}>
            Give at least two. Mark exactly one as correct.
          </p>
          {LETTERS.map((k) => (
            <div key={k} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input type="radio" name="c-correct" value={k} checked={correct === k}
                     onChange={() => setCorrect(k)}
                     aria-label={`Mark option ${k.toUpperCase()} as the correct answer`} />
              <label htmlFor={`c-opt-${k}`} className="visually-hidden">Option {k.toUpperCase()}</label>
              <span aria-hidden="true" style={{ fontWeight: 700, width: '1.5rem' }}>{k.toUpperCase()}.</span>
              <input id={`c-opt-${k}`} value={options[k] ?? ''}
                     onChange={(e) => setOptions({ ...options, [k]: e.target.value })}
                     style={{ ...field, flex: 1 }} />
            </div>
          ))}
        </fieldset>

        <label htmlFor="c-rationale" style={{ marginTop: '1rem' }}>
          Why is that the answer? <span className="muted">(strongly encouraged)</span>
        </label>
        <textarea id="c-rationale" rows={3} value={rationale}
                  onChange={(e) => setRationale(e.target.value)} style={field} />

        {error && <div className="notice notice--warn" role="alert" style={{ marginTop: '1rem' }}>{error}</div>}
        {sent && (
          <div className="notice" role="status" style={{ marginTop: '1rem' }}>
            <strong>Sent for review.</strong> You will see it below, and earn cookies if it is accepted.
          </div>
        )}

        <div className="btn-row" style={{ marginTop: '1.25rem' }}>
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Submit for review'}
          </button>
        </div>
      </form>

      <h2>Your submissions</h2>
      {mine.length === 0 ? (
        <p className="muted">You have not submitted any questions yet.</p>
      ) : (
        <ul className="submission-list">
          {mine.map((c) => (
            <li key={c.id} className="card card--plain">
              <p style={{ marginTop: 0 }}>{c.stem}</p>
              <p className="muted" style={{ marginBottom: 0 }}>
                {c.subject}
                {c.topicName ? ` · ${c.topicName}` : ''} ·{' '}
                <strong>
                  {c.state === 'submitted' ? 'Awaiting review'
                    : c.state === 'accepted' ? '✓ Accepted — 5 cookies'
                    : 'Not accepted'}
                </strong>
                {c.reviewNote ? ` — ${c.reviewNote}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
